// Role-based permission helper. Mirrors backend `ROLE_PERMS`.
// Resources: projects, assets, services, tasks, issues, teams, users
// Actions:   read, create, update, delete

import { useEffect, useState } from "react";
import api from "@/lib/api";

const ROLE_PERMS = {
  admin: "*",
  team_lead: {
    projects: ["read"],
    biz_projects: ["read"],
    assets:   ["read", "create", "update"],
    services: ["read", "create", "update"],
    tasks:    ["read", "create", "update"],
    issues:   ["read", "create", "update"],
    teams:    ["read"],
    users:    ["read"],
    opa_requests: ["read", "create"],
  },
  team_associate: {
    projects: ["read"],
    biz_projects: ["read"],
    assets:   ["read"],
    services: ["read"],
    tasks:    ["read", "update"],
    issues:   ["read", "update"],
    teams:    ["read"],
    users:    ["read"],
    opa_requests: ["read", "create"],
  },
};

// Legacy aliases so existing tokens still work.
const ROLE_ALIASES = { manager: "team_lead", user: "team_associate", accounts: "team_associate" };

export function can(user, resource, action) {
  if (!user) return false;
  const raw = user.role || "team_associate";
  const role = ROLE_ALIASES[raw] || raw;
  const perms = ROLE_PERMS[role];
  if (perms === "*") return true;
  if (!perms) return false;
  const allowed = perms[resource];
  if (!allowed) return false;
  return allowed.includes(action);
}

// Convenience: derive resource name from an "/endpoint" path.
export function resourceFromEndpoint(endpoint) {
  const first = (endpoint || "").replace(/^\//, "").split("/")[0];
  // Frontend endpoint "/biz-projects" maps to RBAC resource "biz_projects"
  return first.replace(/-/g, "_");
}

// Shared cache so multiple hooks don't trigger redundant /me/modules fetches.
let _modulesPromise = null;
function fetchModulesOnce() {
  if (!_modulesPromise) {
    _modulesPromise = api.get("/me/modules")
      .then((r) => r.data)
      .catch(() => ({ modules: [], role: null }));
  }
  return _modulesPromise;
}
// Reset the cache on auth changes (login/logout).
export function resetModulesCache() { _modulesPromise = null; }

// React hook: returns { modules, role } once /me/modules resolves. Cached.
export function useEnabledModules() {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchModulesOnce().then((data) => { if (!cancelled) setMeta(data); });
    return () => { cancelled = true; };
  }, []);
  return meta;
}

// Convenience: is the given resource in "read only" mode for the current user?
// Admin is never read-only. Returns false while `/me/modules` is loading
// (avoids flicker that would briefly hide write UI on first paint).
export function isResourceReadOnly(meta, resource) {
  if (!meta || meta.role === "admin") return false;
  return (meta.modules || []).includes(`${resource}_readonly`);
}
